import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import md5 from 'md5';
import PgIcon from './pg-icon';

const GRAVATAR_PX = {
    xs: 20, sm: 24, md: 32, lg: 40, xl: 48, '2xl': 56, '3xl': 64,
};

const PLACEHOLDER_ICON_PX = {
    xs: 13, sm: 16, md: 20, lg: 24, xl: 28, '2xl': 32, '3xl': 32,
};

const BADGE_ICON_PX = {
    xs: 6, sm: 7, md: 8, lg: 9, xl: 10, '2xl': 11, '3xl': 12,
};

// Deterministic per-name color for the initials fallback, applied via the
// `--av-fg` custom property pegment.css reads on `.pg-av__inner`.
const INITIALS_COLORS = [
    '#e8734a', '#5b8def', '#8a63d2', '#2fa876',
    '#d65c9e', '#4aa3c9', '#c9944a', '#7a7fe0',
];

const hashString = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i += 1) {
        hash = (hash << 5) - hash + str.charCodeAt(i);
        hash |= 0;
    }
    return Math.abs(hash);
};

const getInitials = (name) => {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

const PgAvatar = ({ src, email, name, size, status, badge, shape, active, className, ...props }) => {
    const [imgFailed, setImgFailed] = useState(false);

    const gravatarSrc = useMemo(() => {
        if (src || !email) return null;
        const hash = md5(email.trim().toLowerCase());
        const px = GRAVATAR_PX[size] || GRAVATAR_PX.md;
        return `https://www.gravatar.com/avatar/${hash}?s=${px * 2}&d=404`;
    }, [src, email, size]);

    const imgSrc = src || gravatarSrc;
    // Also the fallback when `src`/Gravatar fails to load (Gravatar is
    // requested with d=404 precisely so a missing photo lands here).
    const initials = name ? getInitials(name) : '';
    // const fgColor = initials ? INITIALS_COLORS[hashString(name) % INITIALS_COLORS.length] : undefined;

    const classes = [
        'pg-av',
        `pg-av--${size}`,
        `pg-av--${shape}`,
        active && 'pg-av--active',
        className,
    ].filter(Boolean).join(' ');

    return (
        <span className={classes} {...props}>
            <span className="pg-av__inner">
                {imgSrc && !imgFailed ? (
                    <img
                        className="pg-av__img"
                        src={imgSrc}
                        alt={name || 'Avatar'}
                        onError={() => setImgFailed(true)}
                    />
                ) : initials ? (
                    initials
                ) : (
                    <PgIcon name="user" className="pg-av__placeholder" size={PLACEHOLDER_ICON_PX[size]} />
                )}
            </span>
            {status && <span className={`pg-av__status pg-av__status--${status}`} />}
            {badge === 'app' && (
                <span className="pg-av__badge">
                    <PgIcon name="paw" size={BADGE_ICON_PX[size]} color="currentColor" />
                </span>
            )}
        </span>
    );
};

PgAvatar.propTypes = {
    /** Direct image URL. Takes priority over `email`'s Gravatar lookup. */
    src: PropTypes.string,
    /** Used to build the Gravatar URL (`d=404`, so a missing photo falls through to initials). Ignored when `src` is set. */
    email: PropTypes.string,
    /** Display name. Drives the initials fallback and its deterministic color, and is used as the placeholder-icon `alt`/fallback when both `src` and `email` are absent. */
    name: PropTypes.string,
    size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl']),
    /** Status dot in the bottom-right corner. Omit for no badge. */
    status: PropTypes.oneOf(['online', 'busy', 'away', 'offline']),
    /** `"app"` renders the brand badge (paw icon) in the bottom-right corner. */
    badge: PropTypes.oneOf(['app']),
    shape: PropTypes.oneOf(['circle', 'square']),
    /** Persistent "active" look (4px border) — the same state `:active`/press applies momentarily. */
    active: PropTypes.bool,
    className: PropTypes.string,
};

PgAvatar.defaultProps = {
    src: undefined,
    email: undefined,
    name: '',
    size: 'md',
    status: undefined,
    badge: undefined,
    shape: 'circle',
    active: false,
    className: '',
};

export default PgAvatar;
